import type { SubsidyReference } from "@/features/subventions/types/subvention.types";
import {
  refBadge, refBlurbs, refFigure, splitEmoji,
} from "@/features/subventions/utils/subventionStatus";

type ReferenceListProps = {
  references: SubsidyReference[]
  variant?: "inline" | "drawer"
};

const refName = (reference: SubsidyReference): string =>
  splitEmoji(reference.name).text || reference.name;

// Compact two-column list used inside the inline card expand.
const InlineList = ({ references }: { references: SubsidyReference[] }) => (
  <ul className="flex flex-col gap-2.5">
    {references.map((reference) => {
      const blurbs = refBlurbs(reference);
      return (
        <li
          key={reference.id}
          className="grid grid-cols-[auto_1fr] items-start gap-x-3 gap-y-1"
        >
          <span className="row-span-2 min-w-[84px] self-start rounded-lg bg-lightprimary px-2.5 py-1.5 text-center text-[13px] font-extrabold text-primary-emphasis">
            {refFigure(reference)}
          </span>
          <span className="text-[13px] font-semibold text-gray-900 dark:text-white">
            {refName(reference)}
          </span>
          {blurbs.length
            ? <span className="text-xs leading-relaxed text-gray-500 dark:text-gray-400">{blurbs.join(" · ")}</span>
            : <span />}
        </li>
      );
    })}
  </ul>
);

// Richer cards used inside the detail drawer.
const DrawerList = ({ references }: { references: SubsidyReference[] }) => (
  <div className="flex flex-col gap-2.5">
    {references.map((reference) => {
      const badge = refBadge(reference);
      const blurbs = refBlurbs(reference);
      return (
        <div
          key={reference.id}
          className="flex items-start gap-3 rounded-xl border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-800/50"
        >
          <span className="min-w-[58px] shrink-0 rounded-lg bg-primary px-2.5 py-1.5 text-center text-[13px] font-extrabold leading-tight text-white">
            {badge.text}
            {badge.sub && (
              <span className="block text-[9px] font-semibold">{badge.sub}</span>
            )}
          </span>
          <div className="min-w-0 flex-1">
            <div className="text-[13px] font-semibold text-gray-900 dark:text-white">{refName(reference)}</div>
            {blurbs.length > 0 && (
              <ul className="mt-1 list-disc pl-4 text-[11.5px] leading-relaxed text-gray-600 dark:text-gray-300">
                {blurbs.map((blurb) => <li key={blurb}>{blurb}</li>)}
              </ul>
            )}
          </div>
        </div>
      );
    })}
  </div>
);

export const ReferenceList = ({ references, variant = "inline" }: ReferenceListProps) => {
  if (!references.length) return null;
  return variant === "drawer"
    ? <DrawerList references={references} />
    : <InlineList references={references} />;
};

ReferenceList.displayName = "ReferenceList";
